import React from 'react';
import pt from 'prop-types';
import { reduce } from 'lodash';
import Icon from 'components/icon';
import Scrollbars from 'components/scrollbars';
import { classNames } from 'utils';
import { wordify } from 'shared/utils';

const TOP_LEVEL_PATH_PRIORITY = {
  layout: 1,
  templates: 2,
  sections: 3,
  snippets: 4,
  assets: 5,
  config: 6,
  locales: 7,
};

export default class StorefrontEditorCodeFileList extends React.PureComponent {
  static propTypes = {
    themeConfigs: pt.object.isRequired,
    onClickFile: pt.func.isRequired,
    activeFile: pt.object,
    openFiles: pt.array,
  };

  state = {
    fileList: [],
    showingItems: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      fileList: this.getInitialFileList(props),
      showingItems: this.getInitialShowingItems(props),
    };
  }

  componentDidUpdate(prevProps) {
    const { themeConfigs, openFiles } = this.props;

    if (prevProps.themeConfigs !== themeConfigs) {
      this.setState({
        fileList: this.getInitialFileList(),
      });
    }

    if (prevProps.openFiles !== openFiles) {
      this.setState({
        showingItems: this.getInitialShowingItems(),
      });
    }
  }

  getInitialFileList(props = this.props) {
    const { themeConfigs } = props;

    const fileList = [];

    // Recursively build file list
    for (const config of themeConfigs?.results || []) {
      if (!config.file_path.startsWith('theme/')) {
        continue;
      }
      const filePath = config.file_path.replace(/^theme\//, '');
      const fileGroup = this.findFileGroup(fileList, filePath);
      if (fileGroup?.files) {
        fileGroup.files.push({
          ...config,
          name: config.file_path.split('/').pop(),
        });
      }
    }

    this.sortFileList(fileList);

    return fileList;
  }

  getInitialShowingItems(props = this.props) {
    const { openFiles } = props;
    const { showingItems } = this.state;

    return {
      ...showingItems,
      ...reduce(
        openFiles,
        (acc, file) => {
          const parts = file.file_path.replace(/^theme\//, '').split('/');
          while (parts.length) {
            const pathId = parts.join('/');
            if (showingItems[pathId] === undefined) {
              acc[pathId] = true;
            }
            parts.pop();
          }
          return acc;
        },
        {},
      ),
    };
  }

  sortFileList(fileList) {
    // Prioritize groups by path name
    return fileList.sort((a, b) => {
      const aPriority = TOP_LEVEL_PATH_PRIORITY[a.path] || Infinity;
      const bPriority = TOP_LEVEL_PATH_PRIORITY[b.path] || Infinity;

      if (aPriority === bPriority) {
        return a.path.localeCompare(b.path);
      }

      return aPriority - bPriority;
    });
  }

  findFileGroup(files, filePath) {
    const parts = filePath.split('/');

    // Remove file name from the path
    parts.pop();

    let fileList = files;
    let path = parts.shift();
    let fullPath = path;

    while (path) {
      let group = fileList.find((group) => group.path === path);

      if (!group) {
        group = {
          id: fullPath,
          path,
          groups: [],
          files: [],
        };
        fileList.push(group);
        fileList = group.groups;
      }

      path = parts.shift();

      if (path) {
        fileList = group.groups;
        fullPath += `/${path}`;
      } else {
        return group;
      }
    }

    return null;
  }

  onClickFilePath = (event) => {
    event.preventDefault();
    const { showingItems } = this.state;
    const { id } = event.currentTarget.dataset;

    this.setState({
      showingItems: {
        ...showingItems,
        [id]: !showingItems[id],
      },
    });
  };

  onClickFile = (event) => {
    event.preventDefault();
    const { id } = event.currentTarget.dataset;
    const { themeConfigs, onClickFile } = this.props;
    const file = themeConfigs.results.find((file) => file.id === id);

    if (onClickFile) {
      onClickFile(file);
    }
  };

  renderFileList(fileList = this.state.fileList) {
    const { activeFile } = this.props;
    const { showingItems } = this.state;

    return fileList.map((group) => {
      const { id, path, groups, files } = group;

      return (
        <div key={id} className="storefront-editor--code-file-list--group">
          <div className="storefront-editor--code-file-list--item">
            <a href="" onClick={this.onClickFilePath} data-id={id}>
              <div className="path">
                <Icon fa="folder" faType="regular" /> {wordify(path)}
              </div>
              {showingItems[id] ? (
                <Icon fa="chevron-up" />
              ) : (
                <Icon fa="chevron-down" />
              )}
            </a>
          </div>
          <div
            className={classNames('storefront-editor--code-file-list--files', {
              open: showingItems[id],
            })}
          >
            {files.map((file) => (
              <div
                key={file.id}
                className={classNames(
                  'storefront-editor--code-file-list--item',
                  { active: file.id === activeFile?.id },
                )}
              >
                <a href="" onClick={this.onClickFile} data-id={file.id}>
                  <div className="path">
                    <Icon fa="file" />
                    {file.name}
                  </div>
                </a>
              </div>
            ))}
            {groups.length > 0 && this.renderFileList(groups)}
          </div>
        </div>
      );
    });
  }

  render() {
    return (
      <div className="storefront-editor--code-file-list--container">
        <Scrollbars>
          <div className="storefront-editor--code-file-list">
            {this.renderFileList()}
          </div>
        </Scrollbars>
      </div>
    );
  }
}
